import { gql } from '@apollo/client'
import { Button, Textarea, TextareaElement, useToast } from '@farol-ds/react'
import { LoginTrigger } from '@jusbrasil-web/shared-bifrost'
import { useGA4Dispatcher } from '@jusbrasil-web/shared-ga4'
import { COMPONENT_CLASS, GA4ClickEvent, SELECTED_OPTION } from '@jusbrasil-web/shared-ga4-events'
import { ChangeEvent, useRef, useState } from 'react'

import { useAddComment, useAddReply, useEditTextComment } from '../hooks'
import {
  PublishComment_CommentFragment,
  PublishComment_DocumentFragment,
  PublishComment_ViewerFragment,
} from './__generated__/publish-comment.graphql'
import styles from './styles.module.scss'

export interface PublishCommentProps {
  document: PublishComment_DocumentFragment
  viewer: PublishComment_ViewerFragment | null
  comment?: PublishComment_CommentFragment
  isReply?: boolean
  onButtonClick?: () => void
  isEditMode?: boolean
}

export function PublishComment(props: PublishCommentProps) {
  const { document, viewer, comment, isReply, onButtonClick, isEditMode } = props
  const toast = useToast()
  const [commentText, setCommentText] = useState('')
  const textAreaRef = useRef<TextareaElement>(null)
  const { sendGA4Event } = useGA4Dispatcher()

  const { addComment, loading: loadingAddComment } = useAddComment({
    document,
    commentText,
    commentId: comment?.id,
  })
  const { addReply, loading: loadingAddReply } = useAddReply({
    document,
    commentText,
    commentId: comment?.id,
  })
  const { editTextComment, loading: loadingEdit } = useEditTextComment({
    document,
    commentText,
    comment: comment as PublishComment_CommentFragment,
  })

  const loading = loadingEdit || loadingAddReply || loadingAddComment

  const handleTextAreaChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setCommentText(e.target.value)
  }

  async function handlePublish() {
    if (!commentText || !viewer || (isEditMode && !comment)) return

    sendGA4Event(
      new GA4ClickEvent({
        componentClass: COMPONENT_CLASS.DOCUMENT_FOOTER,
        componentName: null,
        selectedOption: SELECTED_OPTION.COMMENT_DOCUMENT,
      })
    )

    if (isEditMode) {
      editTextComment()
        .then(onButtonClick)
        .catch(() => toast('Erro ao editar comentário', { type: 'negative' }))
      return
    }

    if (isReply) {
      addReply()
        .then(() => {
          onButtonClick?.()
          toast('Comentário publicado', { id: 'reply-comment' })
        })
        .catch(() => toast('Erro ao responder comentário', { type: 'negative' }))
      return
    }

    addComment()
      .then(() => {
        if (textAreaRef.current) textAreaRef.current.value = ''
        onButtonClick?.()
        toast('Comentário publicado', { id: 'add-comment' })
      })
      .catch(() => toast('Erro ao publicar comentário', { type: 'negative' }))
  }

  return (
    <div className={styles.container}>
      <Textarea
        className={styles.commentText}
        onValueChange={handleTextAreaChange}
        placeholder="Escreva aqui..."
        defaultValue={isEditMode && comment ? comment.text : ''}
        ref={textAreaRef}
      />

      <div className={styles.publishButton}>
        <LoginTrigger
          isLogged={!!viewer}
          options={{
            eventData: {
              from_component: 'docview/PublishComment',
            },
          }}
        >
          <Button onClick={handlePublish} loading={loading}>
            Comentar
          </Button>
          {(isReply || isEditMode) && <Button onClick={onButtonClick}>Cancelar</Button>}
        </LoginTrigger>
      </div>
    </div>
  )
}

PublishComment.fragments = {
  comment: gql`
    fragment PublishComment_comment on DocumentComment {
      id
      text
    }
  `,
  document: gql`
    fragment PublishComment_document on Document {
      docId
      artifact
    }
  `,
  viewer: gql`
    fragment PublishComment_viewer on Profile {
      pid
    }
  `,
}
